import { ApiResponse, AsyncRequestTransform } from 'apisauce';
import { AxiosRequestConfig } from 'axios';
import { isAfter, sub } from 'date-fns';

import type { Authentication } from '@barboxx/models/types';

import { api } from './api';

// Generated by Copilot
interface SerializedException {
    status: number;
    message: string;
    [key: string]: unknown;
}

export class ClientApiError extends Error {
    public readonly status: number;
    public readonly details?: unknown;

    constructor(status: number, message: string, details?: unknown) {
        super(message);
        this.name = 'ClientApiError';
        this.status = status;
        this.details = details;
    }
}

const isSerializedException = (
    value: unknown
): value is SerializedException => {
    if (!value || typeof value !== 'object') {
        return false;
    }

    return (
        'status' in value &&
        typeof (value as SerializedException).status === 'number' &&
        (value as SerializedException).status >= 400
    );
};

const getErrorMessage = (value: unknown, fallback = 'Unknown Error') => {
    if (value instanceof Error && value.message) {
        return value.message;
    }

    if (
        value &&
        typeof value === 'object' &&
        'message' in value &&
        typeof value.message === 'string'
    ) {
        return value.message;
    }

    return fallback;
};

const toError = (value: unknown, fallback = 'Unknown Error') =>
    value instanceof Error
        ? value
        : new Error(getErrorMessage(value, fallback));

// Helper functions
export const serializeJSONToUrl = (data: Record<string, string>) =>
    Object.keys(data)
        .map(keyName => {
            if (keyName === 'location') {
                return `${encodeURIComponent(keyName)}=${data[keyName]}`;
            }
            return `${encodeURIComponent(keyName)}=${encodeURIComponent(
                data[keyName]
            )}`;
        })
        .join('&');

// publicApi.addResponseTransform(response => {
//     if (!response.ok) {
//         switch (response.problem) {
//             case 'NETWORK_ERROR':
//                 console.log('what the error data here', response.data);
//                 response.data = {
//                     message: 'Network Error',
//                 };
//                 break;
//             case 'CLIENT_ERROR':
//                 console.log('public api client error', response.status);
//                 if (response.status === 401) {
//                     // try to solve the 401
//                     response.data = {
//                         message: 'Unauthorized',
//                     };
//                     // await logout();
//                     // window.location.assign('/');
//                     // return;
//                 }
//                 break;
//         }
//         throw response.data;
//     }
// });

export const bearerToken = (token: string) => `Bearer ${token}`;

interface RequestTransformCallbacks {
    refreshExpiredToken: () => Promise<
        Authentication.UserAuthReturn | Authentication.CustomerAuthReturn
    >;
}

interface RequestConfig extends AxiosRequestConfig {
    headers: {
        ExpiresAt: string;
        Authorization: string;
        LockerId: string;
    };
}

export const checkExpiredToken = (expiresAt: string) =>
    expiresAt
        ? isAfter(new Date(), sub(new Date(expiresAt), { seconds: 3 }))
        : true;

export const apiAddAsyncRequestTransform =
    ({
        refreshExpiredToken,
    }: RequestTransformCallbacks): AsyncRequestTransform =>
    request =>
    async () => {
        // check to see if token needs refresh before calling new call here maybe
        const { headers } = request as RequestConfig;

        const tokenExpired = checkExpiredToken(headers.ExpiresAt);

        // console.log('is expired check', tokenExpired, headers.expiresAt);

        if (tokenExpired) {
            try {
                const { expiresAt, token } = await refreshExpiredToken();

                headers['Authorization'] = bearerToken(token);
                headers['ExpiresAt'] = expiresAt;

                api.setTokenInfo({ token, expiresAt });
            } catch (error) {
                throw new ClientApiError(
                    401,
                    getErrorMessage(error, 'Invalid refresh token'),
                    {
                        sourceError: error,
                    }
                );
            }
        }
    };

interface ResponseCallbacks {
    unauthorizedCallback?: (error: Error) => Promise<void>;
    networkErrorCallback?: (error: Error) => void;
}

export const addAsyncResponseTransform =
    ({ unauthorizedCallback, networkErrorCallback }: ResponseCallbacks) =>
    async (response: ApiResponse<unknown, Error>) => {
        if (!response.ok) {
            let error: unknown = response?.data ?? new Error('Unknown Error');

            if (response.status === 401) {
                const unauthorizedError = new ClientApiError(
                    401,
                    getErrorMessage(error, 'Unauthorized'),
                    error
                );

                if (unauthorizedCallback) {
                    try {
                        await unauthorizedCallback(
                            toError(error, 'Unauthorized')
                        );
                    } catch (callbackError) {
                        throw new ClientApiError(
                            401,
                            getErrorMessage(error, 'Unauthorized'),
                            {
                                sourceError: error,
                                callbackError,
                            }
                        );
                    }
                }

                throw unauthorizedError;
            }

            switch (response.problem) {
                case 'NETWORK_ERROR':
                    error = new ClientApiError(
                        response.status ?? 0,
                        'Check your internet connection and try again.',
                        {
                            sourceError: response?.data,
                            problem: response.problem,
                        }
                    );

                    if (networkErrorCallback) {
                        await networkErrorCallback(
                            toError(
                                error,
                                'Check your internet connection and try again.'
                            )
                        );
                    }
                    break;
                case 'CLIENT_ERROR':
                    // error = {
                    //     message: 'Client Error',
                    // };
                    break;
            }

            throw error instanceof Error
                ? error
                : new ClientApiError(
                      response.status ?? 0,
                      getErrorMessage(error),
                      error
                  );
        }
    };

export function throwIfBadResponse<T, U>(
    call: Promise<ApiResponse<T, U>>,
    defaultError = 'Unknown Error'
) {
    return call.then(({ data, ok, problem, originalError, status }) => {
        if (!data || !ok) {
            const errorMessage = getErrorMessage(
                data,
                getErrorMessage(originalError, defaultError)
            );

            throw new ClientApiError(status ?? 0, errorMessage, {
                problem,
                originalError,
                responseData: data,
                status,
            });
        }

        return data;
    });
}
/**
 * Awaits a promise that may resolve to either a value of type T or a serialized exception (as may occur over WebSocket acknowledgments).
 * If the response appears to be a serialized exception (i.e., has a numeric `status` property >= 400), it rethrows it as a client API error.
 *
 * @template T The expected success type of the response.
 * @param {Promise<T | SerializedException>} call - A promise that resolves to either a value of type T or a serialized exception object.
 * @returns {Promise<T>} A promise that resolves to the value of type T, or rejects with a client API error if an error is detected.
 *
 * @example
 * // Usage with a WebSocket ack handler:
 * throwIfBadAck(socket.emitWithAck('someEvent', payload))
 *   .then(result => {
 *     // handle success
 *   })
 *   .catch(err => {
 *     // handle client API error
 *   });
 */
export function throwIfBadAck<T>(call: Promise<T | SerializedException>) {
    return call.then(response => {
        // Check if response is a serialized exception from the server.
        // The class type is lost over websockets, so we check for properties.
        if (isSerializedException(response)) {
            throw new ClientApiError(
                response.status,
                response.message,
                response
            );
        }

        return response as T;
    });
}
